import RelayIcon from '@/components/RelayIcon'
import UserAvatar from '@/components/UserAvatar'
import Username from '@/components/Username'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Folder01Icon,
  Tick01Icon,
  Cancel01Icon,
  Delete01Icon,
  Loading03Icon
} from '@hugeicons/core-free-icons'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useSecondaryPage } from '@/PageManager'
import { normalizeUrl } from '@/lib/url'
import { canWriteAtom, useCurrentPubkey } from '@/atoms/session'
import client from '@/services/client.service'
import { now } from '@/lib/utils'
import * as kinds from '@nostr/tools/kinds'
import { invalidateFavorites } from '@/atoms/invalidation'
import { loadFavoriteRelays } from '@nostr/gadgets/lists'
import { getReplaceableCoordinateFromEvent } from '@/lib/event'
import { switchFeed } from '@/atoms/feed'
import { useState, useRef, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { ResolvedSet } from '@nostr/gadgets/sets'
import { toast } from 'sonner'
import { useAtomValue } from 'jotai'

export default function RelaySetInfo({
  relaySet,
  className
}: {
  relaySet: ResolvedSet<string>
  className?: string
}) {
  const { t } = useTranslation()
  const { push: pushSecondaryPage } = useSecondaryPage()
  const accountPubkey = useCurrentPubkey()
  const canWrite = useAtomValue(canWriteAtom)
  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const canEdit = !deleting && canWrite && accountPubkey === relaySet.pointer.pubkey

  const [editValue, setEditValue] = useState(relaySet.title)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus()
  }, [editing])

  return (
    <div className={cn('space-y-4 mb-2', className)}>
      <div className="px-4 space-y-4">
        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2 truncate min-w-0">
            <HugeiconsIcon icon={Folder01Icon} className="w-8 h-8 shrink-0" />
            {editing ? (
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <Input
                  ref={inputRef}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSave()
                    if (e.key === 'Escape') handleCancel()
                  }}
                  className="h-9 text-lg font-semibold flex-1 min-w-0"
                  disabled={saving}
                />
                {saving ? (
                  <HugeiconsIcon icon={Loading03Icon} className="animate-spin size-4" />
                ) : (
                  <>
                    <button onClick={handleSave} className="shrink-0 p-1">
                      <HugeiconsIcon icon={Tick01Icon} className="size-5" />
                    </button>
                    <button onClick={handleCancel} className="shrink-0 p-1">
                      <HugeiconsIcon icon={Cancel01Icon} className="size-5" />
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div
                className="text-2xl font-semibold truncate select-text cursor-text hover:bg-accent/50 rounded px-1 -mx-1"
                onClick={() => {
                  if (canEdit) {
                    setEditValue(relaySet.title)
                    setEditing(true)
                  }
                }}
              >
                {relaySet.title} ({relaySet.items.length})
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {deleting ? (
              <HugeiconsIcon icon={Loading03Icon} className="animate-spin size-4" />
            ) : (
              canEdit &&
              !editing && (
                <button onClick={handleDelete} className="shrink-0 p-1 text-destructive">
                  <HugeiconsIcon icon={Delete01Icon} className="size-5" />
                </button>
              )
            )}
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-semibold text-muted-foreground">{t('Relays')}</div>
          <div className="space-y-1">
            {relaySet.items.map((url) => (
              <div
                key={url}
                className="flex items-center gap-3 cursor-pointer hover:text-foreground"
                onClick={() => pushSecondaryPage('/?r=' + encodeURIComponent(normalizeUrl(url)))}
              >
                <RelayIcon url={url} className="w-4 h-4" iconSize={10} />
                <span className="text-muted-foreground text-sm truncate">{url}</span>
              </div>
            ))}
          </div>
        </div>

        {accountPubkey !== relaySet.pointer.pubkey && (
          <div className="space-y-2">
            <div className="text-sm font-semibold text-muted-foreground">{t('Author')}</div>
            <div className="flex gap-2 items-center">
              <UserAvatar user={relaySet.pointer.pubkey} size="small" />
              <Username user={relaySet.pointer.pubkey} className="font-semibold" />
            </div>
          </div>
        )}
      </div>
    </div>
  )

  async function handleSave() {
    const trimmed = editValue.trim()
    if (!trimmed || trimmed === relaySet.title) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      const event = await client.publish({
        kind: kinds.Relaysets,
        content: '',
        tags: [
          ['d', relaySet.pointer.identifier],
          ['title', trimmed],
          ...relaySet.items.map((url) => ['relay', url])
        ],
        created_at: now()
      })
      if (!event) {
        console.warn('failed to publish updated relay set event')
        toast.error(t('Failed to publish updated relay set event.'))
        return
      }

      invalidateFavorites()
      setEditing(false)
      switchFeed({
        relaySet: { ...relaySet, title: trimmed, event }
      })
    } finally {
      setSaving(false)
    }
  }

  function handleCancel() {
    setEditValue(relaySet.title)
    setEditing(false)
  }

  async function handleDelete() {
    if (!relaySet.event) return
    if (relaySet.items.length > 3 && !window.confirm(t('Delete this relay set?'))) return
    setDeleting(true)

    try {
      await client.attemptDelete(relaySet.event)

      const coordinate = getReplaceableCoordinateFromEvent(relaySet.event)
      const fr = await loadFavoriteRelays(accountPubkey!, [], true)
      const tags = fr.event
        ? fr.event.tags.filter((t) => !(t[0] === 'a' && t[1] === coordinate))
        : []

      const favEvent = await client.publish({
        kind: kinds.FavoriteRelays,
        content: '',
        tags,
        created_at: now()
      })
      if (favEvent) {
        await loadFavoriteRelays(favEvent.pubkey, undefined, favEvent)
      }
      invalidateFavorites()
    } catch (err) {
      console.warn('failed to delete:', (err as Error).message)
      toast.error(t('Failed to delete: {{error}}', { error: (err as Error).message }))
    }
  }
}
